// ErrorBoundary.jsx
import React from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';

const SENTRY_REPORT_DIALOG_COMMENT_ID = 'id_comments';

class ErrorBoundary extends React.PureComponent {
  state = { error: null, eventId: null };

  async componentDidCatch(error, info) {
    const { context } = this.props;
    this.setState({ error });
    Sentry.withScope(scope => {
      scope.setTag('record', 'error-boundary');
      Object.keys(info).forEach(key => {
        scope.setExtra(key, info[key]);
      });
      scope.setExtra('context', context);
      scope.setExtra('errorMessage', error?.message ?? 'No error message');
      scope.setExtra('errorStack', error?.stack ?? 'No error stack');
      scope.setExtra('errorDigest', error?.digest ?? 'No error digest');
      const eventId = Sentry.captureException(error);
      this.setState({ eventId });
    });
  }

  render() {
    const { error, eventId } = this.state;
    const { children, errorElement, onErrorResolved } = this.props;
    if (error) {
      return React.cloneElement(errorElement, {
        reportError: () =>
          Sentry.showReportDialog({
            eventId,
            onLoad: () => {
              document.querySelector(
                `#${SENTRY_REPORT_DIALOG_COMMENT_ID}`
              ).textContent = `Current URL: ${location.href}`;
            },
          }),
        onErrorResolved: () => {
          onErrorResolved();
          this.setState({ error: null, eventId: null });
        },
        from: 'ErrorBoundary',
        error,
      });
    }
    return children;
  }
}

ErrorBoundary.propTypes = {
  context: PropTypes.string,
  errorElement: PropTypes.node,
  children: PropTypes.node,
  onErrorResolved: PropTypes.func,
};

ErrorBoundary.defaultProps = {
  context: '',
  errorElement: 'Oops! Something went wrong. :(',
  children: '',
  onErrorResolved: () => null,
};

export default ErrorBoundary;
